"use client";

import { ReactElement, ReactNode, cloneElement, createContext, isValidElement, useContext, useEffect, useRef, useState } from "react";

export type DropdownPlacement = "bottom-right" | "bottom-center" | "right-top" | "right-bottom";

type DropdownControls = {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
};

interface DropdownProps {
    children: ReactNode;
    placement?: DropdownPlacement;
    className?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    role?: "menu" | "listbox";
    ariaLabel?: string;
}

interface DropdownTriggerProps {
    children: ReactNode;
    className?: string;
    asChild?: boolean;
}

interface DropdownContentProps {
    children: ReactNode | ((props: DropdownControls) => ReactNode);
    className?: string;
}

type DropdownContextValue = DropdownControls & {
    placement: DropdownPlacement;
    role: "menu" | "listbox";
    ariaLabel?: string;
};

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdownContext() {
    const ctx = useContext(DropdownContext);
    if (!ctx) {
        throw new Error("DropdownTrigger et DropdownContent doivent etre utilises dans Dropdown.");
    }
    return ctx;
}

function getMenuPositionClasses(placement: DropdownPlacement) {
    switch (placement) {
        case "bottom-right":
            return "top-full right-0 mt-1";
        case "bottom-center":
            return "top-full left-1/2 -translate-x-1/2 mt-1";
        case "right-top":
            return "left-full top-0 ml-1";
        case "right-bottom":
            return "left-full bottom-0 ml-1";
        default:
            return "left-full bottom-0 ml-1";
    }
}

export default function Dropdown({
    children,
    placement = "right-bottom",
    className = "",
    open,
    onOpenChange,
    role = "menu",
    ariaLabel,
}: DropdownProps) {
    const [internalOpen, setInternalOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const isControlled = typeof open === "boolean";
    const isOpen = isControlled ? (open as boolean) : internalOpen;

    const setOpen = (nextOpen: boolean) => {
        if (!isControlled) {
            setInternalOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
    };

    useEffect(() => {
        if (!isOpen) return;

        const onMouseDown = (e: MouseEvent) => {
            const wrap = wrapperRef.current;
            if (!wrap) return;
            if (e.target instanceof Node && !wrap.contains(e.target)) {
                setOpen(false);
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen]);

    const controls: DropdownControls = {
        isOpen,
        toggle: () => setOpen(!isOpen),
        close: () => setOpen(false),
    };

    return (
        <DropdownContext.Provider
            value={{
                ...controls,
                placement,
                role,
                ariaLabel,
            }}
        >
            <div className={`relative ${className}`} ref={wrapperRef}>
                {children}
            </div>
        </DropdownContext.Provider>
    );
}

export function DropdownTrigger({ children, className = "", asChild = false }: DropdownTriggerProps) {
    const { isOpen, toggle } = useDropdownContext();

    if (asChild && isValidElement(children)) {
        const childElement = children as ReactElement<Record<string, unknown>>;
        return cloneElement(childElement, {
            "aria-haspopup": "menu",
            "aria-expanded": isOpen,
            onClick: () => {
                const originalOnClick = childElement.props.onClick as (() => void) | undefined;
                originalOnClick?.();
                toggle();
            },
            className: [childElement.props.className as string | undefined, className].filter(Boolean).join(" "),
        });
    }

    return (
        <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            className={className}
            onClick={toggle}
        >
            {children}
        </button>
    );
}

export function DropdownContent({ children, className = "" }: DropdownContentProps) {
    const { isOpen, toggle, close, placement, role, ariaLabel } = useDropdownContext();

    if (!isOpen) return null;

    return (
        <div
            role={role}
            aria-label={ariaLabel}
            className={`absolute z-50 ${getMenuPositionClasses(placement)} ${className}`}
        >
            {typeof children === "function" ? children({ isOpen, toggle, close }) : children}
        </div>
    );
}
